import type { Alert } from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  CircleCheckIcon,
  CircleHelpIcon,
  CircleXIcon,
  type LucideIcon,
  MinusIcon,
  ShieldAlertIcon,
  TriangleAlertIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ChartFrame, type Column } from "../charts/frame";
import { Sparkline } from "../charts/sparkline";
import { type AdminFormat, useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { useInstallationLabel } from "../shell";
import { type CoreOutputs, useTRPC } from "../trpc";
import { useView } from "../view";

type Overview = CoreOutputs["overview"];
type InstallationOverview = Overview["installations"][number];
type KeyFigure = NonNullable<InstallationOverview["keyFigures"]>[number];

const STATES: Record<InstallationOverview["state"], { icon: LucideIcon; tone: string }> = {
  up: { icon: CircleCheckIcon, tone: "text-ok-fg" },
  down: { icon: CircleXIcon, tone: "text-missing-fg" },
  internal_only: { icon: TriangleAlertIcon, tone: "text-partial-fg" },
  unknown: { icon: CircleHelpIcon, tone: "text-muted-foreground" },
};

export function OverviewPage() {
  const trpc = useTRPC();
  const overview = useQuery(trpc.overview.queryOptions());
  return (
    <QueryView query={overview}>
      {(data) => (
        <div className="space-y-6">
          <Installations data={data} />
          <Alerts alerts={data.alerts} />
          <KeyFigures data={data} />
          <Server data={data} />
        </div>
      )}
    </QueryView>
  );
}

function StateLabel({ state }: { state: InstallationOverview["state"] }) {
  const { t } = useTranslation();
  const { icon: Icon, tone } = STATES[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm font-medium", tone)}>
      <Icon className="size-4" aria-hidden />
      {t(`adminApp.states.${state}`)}
    </span>
  );
}

function Installations({ data }: { data: Overview }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const label = useInstallationLabel();
  const columns: Column[] = [
    { key: "installation", label: t("adminApp.installation") },
    { key: "state", label: t("adminApp.overview.state") },
    { key: "version", label: t("adminApp.overview.version") },
    {
      key: "uptimeSeconds",
      label: t("adminApp.overview.uptime"),
      numeric: true,
      format: (v) => format.duration(v as number),
    },
    {
      key: "reportAt",
      label: t("adminApp.overview.report"),
      format: (v) => format.dateTime(v as string),
    },
  ];
  const rows = data.installations.map((i) => ({
    installation: i.name,
    state: i.state,
    version: i.version,
    uptimeSeconds: i.uptimeSeconds,
    reportAt: i.reportAt,
  }));
  return (
    <ChartFrame
      title={t("adminApp.overview.installations")}
      csvName="installations"
      columns={columns}
      rows={rows}
    >
      {data.installations.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("adminApp.overview.noInstallations")}</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {data.installations.map((i) => (
            <section key={i.name} className="rounded-lg border border-border p-3">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="font-semibold">{label(i.name)}</h2>
                <StateLabel state={i.state} />
              </div>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
                <dt className="text-muted-foreground">{t("adminApp.overview.version")}</dt>
                <dd className="tabular">{i.version ?? "–"}</dd>
                <dt className="text-muted-foreground">{t("adminApp.overview.uptime")}</dt>
                <dd>{format.duration(i.uptimeSeconds)}</dd>
                <dt className="text-muted-foreground">{t("adminApp.overview.report")}</dt>
                <dd>{i.reportAt ? format.ago(i.reportAt) : t("adminApp.overview.noReport")}</dd>
              </dl>
            </section>
          ))}
        </div>
      )}
    </ChartFrame>
  );
}

/** An alert's figure in its unit, as the message names it. */
function alertValue(alert: Alert, format: AdminFormat, which: "value" | "threshold"): string {
  const n = alert[which];
  if (n === undefined) return "–";
  switch (alert.kind) {
    case "memory_high":
    case "disk_full":
    case "swap_used":
    case "server_errors":
    case "release_errors":
      return format.percent(n);
    case "backup_old":
    case "offsite_old":
      return format.duration(n * 3600);
    case "release_slower":
      return format.ms(n);
    case "uploads":
      return format.bytes(n);
    default:
      return format.count(n);
  }
}

function Alerts({ alerts }: { alerts: readonly Alert[] }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const label = useInstallationLabel();
  const message = (alert: Alert) => {
    if (
      (alert.kind === "backup_old" || alert.kind === "offsite_old") &&
      alert.value === undefined
    ) {
      return t(`adminApp.alerts.${alert.kind}_none`);
    }
    return t(`adminApp.alerts.${alert.kind}`, {
      // The countable ones have a singular and a plural (…_one, …_other).
      count: alert.value ?? 0,
      installation: alert.installation ? label(alert.installation) : "",
      value: alertValue(alert, format, "value"),
      threshold: alertValue(alert, format, "threshold"),
      subject: alert.subject ?? "",
    });
  };
  const columns: Column[] = [
    { key: "severity", label: t("adminApp.alerts.severity") },
    { key: "kind", label: t("adminApp.alerts.kind") },
    { key: "installation", label: t("adminApp.installation") },
    { key: "message", label: t("adminApp.alerts.message") },
  ];
  const rows = alerts.map((a) => ({
    severity: a.severity,
    kind: a.kind,
    installation: a.installation,
    message: message(a),
  }));
  return (
    <ChartFrame title={t("adminApp.alerts.title")} csvName="alerts" columns={columns} rows={rows}>
      {alerts.length === 0 ? (
        <p className="flex items-center gap-1.5 text-sm text-ok-fg">
          <CircleCheckIcon className="size-4" aria-hidden />
          {t("adminApp.alerts.none")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {alerts.map((alert, i) => {
            const critical = alert.severity === "critical";
            const Icon = critical ? CircleXIcon : TriangleAlertIcon;
            return (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: alerts have no id; their order is stable
                key={i}
                className={cn(
                  "flex items-start gap-2 rounded-lg px-3 py-2 text-sm",
                  critical ? "bg-missing-bg text-missing-fg" : "bg-partial-bg text-partial-fg",
                )}
              >
                <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span className="sr-only">
                  {critical ? t("adminApp.alerts.critical") : t("adminApp.alerts.warning")}:
                </span>
                <span className="min-w-0 flex-1">{message(alert)}</span>
                {alert.misuse ? (
                  <Badge tone="outline" className="shrink-0">
                    <ShieldAlertIcon aria-hidden />
                    {t("adminApp.alerts.misuse")}
                  </Badge>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </ChartFrame>
  );
}

function Change({
  label,
  growth,
  write,
}: {
  label: string;
  growth: KeyFigure["change30"];
  write: (n: number) => string;
}) {
  const format = useAdminFormat();
  if (!growth) return null;
  const Icon =
    growth.change > 0 ? ArrowUpRightIcon : growth.change < 0 ? ArrowDownRightIcon : MinusIcon;
  return (
    <p className="text-xs text-muted-foreground">
      <Icon
        className={cn(
          "mr-1 inline size-3.5 align-[-2px]",
          growth.change > 0 ? "text-ok-fg" : growth.change < 0 && "text-missing-fg",
        )}
        aria-hidden
      />
      <span className="font-medium text-foreground tabular">{format.growth(growth, write)}</span>{" "}
      {label}
    </p>
  );
}

function KeyFigures({ data }: { data: Overview }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const { installation } = useView();
  const label = useInstallationLabel();
  const current = data.installations.find((i) => i.name === installation);
  const figures = current?.keyFigures ?? null;
  const name = (f: KeyFigure) => t(`adminApp.figures.${f.figure.replace(".", "_") as "spend"}`);
  const value = (f: KeyFigure, n: number | null) =>
    f.figure === "spend" && f.currency ? format.money(n, f.currency) : format.count(n);
  const periods = figures?.[0]?.periods ?? [];
  const columns: Column[] = [
    { key: "figure", label: t("adminApp.overview.figure") },
    { key: "currency", label: t("adminApp.overview.currency") },
    ...periods.map((period) => ({ key: period, label: period, numeric: true })),
  ];
  const rows = (figures ?? []).map((f) => ({
    figure: f.figure,
    currency: f.currency ?? null,
    ...Object.fromEntries(periods.map((period, i) => [period, f.points[i] ?? null])),
  }));
  return (
    <ChartFrame
      title={t("adminApp.overview.keyFigures", { installation: label(installation) })}
      description={t("adminApp.overview.keyFiguresHint")}
      csvName={`key-figures-${installation}`}
      columns={columns}
      rows={rows}
    >
      {figures === null ? (
        <p className="text-sm text-muted-foreground">
          {data.core.databaseUp
            ? t("adminApp.overview.noFigures")
            : t("adminApp.overview.figuresNeedDatabase")}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {figures.map((f) => (
            <section key={f.figure} className="rounded-lg border border-border p-3">
              <h2 className="text-sm text-muted-foreground">{name(f)}</h2>
              <div className="mt-1 flex items-end justify-between gap-3">
                <p className="text-2xl font-semibold tracking-tight">{value(f, f.value)}</p>
                <Sparkline
                  points={f.points}
                  label={t("adminApp.overview.trend", {
                    figure: name(f),
                    from: value(f, f.points.find((p) => p !== null) ?? null),
                    to: value(f, [...f.points].reverse().find((p) => p !== null) ?? null),
                  })}
                />
              </div>
              <div className="mt-2 space-y-0.5">
                <Change
                  label={t("adminApp.overview.over30Days")}
                  growth={f.change30}
                  write={(n) => value(f, n)}
                />
                <Change
                  label={t("adminApp.overview.over12Months")}
                  growth={f.change12m}
                  write={(n) => value(f, n)}
                />
              </div>
            </section>
          ))}
        </div>
      )}
    </ChartFrame>
  );
}

/** How full something is, against the share from which it raises an alert. */
function Meter({ share, alertAt, label }: { share: number; alertAt: number; label: string }) {
  const over = share > alertAt;
  return (
    <div className="h-2 w-full rounded-full bg-muted" role="img" aria-label={label}>
      <div
        className={cn("h-2 rounded-full", over ? "bg-missing" : "bg-chart-1")}
        style={{ width: `${Math.min(100, Math.max(1, share * 100))}%` }}
      />
    </div>
  );
}

function ServerRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] items-start gap-3 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function Server({ data }: { data: Overview }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const label = useInstallationLabel();
  const host = data.host;
  const na = t("adminApp.server.na");
  return (
    <Card className="p-4">
      <h2 className="mb-1 text-sm font-semibold tracking-tight">{t("adminApp.server.title")}</h2>
      <dl className="divide-y divide-border">
        <ServerRow label={t("adminApp.server.disk")}>
          {host?.disk ? (
            <div className="space-y-1">
              <p>
                {t("adminApp.server.used", {
                  used: format.bytes(host.disk.usedBytes),
                  total: format.bytes(host.disk.totalBytes),
                  share: format.percent(host.disk.usedBytes / host.disk.totalBytes),
                })}
              </p>
              <Meter
                share={host.disk.usedBytes / host.disk.totalBytes}
                alertAt={0.85}
                label={format.percent(host.disk.usedBytes / host.disk.totalBytes)}
              />
            </div>
          ) : (
            na
          )}
        </ServerRow>
        <ServerRow label={t("adminApp.server.memory")}>
          {host?.memory
            ? t("adminApp.server.available", {
                available: format.bytes(host.memory.availableBytes),
                total: format.bytes(host.memory.totalBytes),
              })
            : na}
        </ServerRow>
        <ServerRow label={t("adminApp.server.swap")}>
          {host?.swap && host.swap.totalBytes > 0 ? (
            <div className="space-y-1">
              <p>
                {t("adminApp.server.used", {
                  used: format.bytes(host.swap.usedBytes),
                  total: format.bytes(host.swap.totalBytes),
                  share: format.percent(host.swap.usedBytes / host.swap.totalBytes),
                })}
              </p>
              <Meter
                share={host.swap.usedBytes / host.swap.totalBytes}
                alertAt={0.5}
                label={format.percent(host.swap.usedBytes / host.swap.totalBytes)}
              />
            </div>
          ) : (
            na
          )}
        </ServerRow>
        <ServerRow label={t("adminApp.server.load")}>
          {host?.load
            ? `${host.load.map((n) => format.decimal(n)).join(" · ")} (${t("adminApp.server.loadPeriods")})`
            : na}
        </ServerRow>
        <ServerRow label={t("adminApp.server.backups")}>
          {host?.backup ? (
            <ul className="space-y-0.5">
              <li>
                {t("adminApp.server.dump")}:{" "}
                {host.backup.dumpAt ? format.ago(host.backup.dumpAt) : t("adminApp.server.never")}
                {host.backup.dumpBytes ? ` · ${format.bytes(host.backup.dumpBytes)}` : ""}
              </li>
              <li>
                {t("adminApp.server.offsite")}:{" "}
                {!host.backup.offsiteConfigured
                  ? t("adminApp.server.notConfigured")
                  : host.backup.offsiteAt
                    ? format.ago(host.backup.offsiteAt)
                    : t("adminApp.server.never")}
              </li>
            </ul>
          ) : (
            na
          )}
        </ServerRow>
        <ServerRow label={t("adminApp.server.certificates")}>
          <ul className="space-y-0.5">
            {data.installations.map((i) => (
              <li key={i.name}>
                {label(i.name)}:{" "}
                {i.certificateDaysLeft === null
                  ? na
                  : t("adminApp.server.daysLeft", { days: i.certificateDaysLeft })}
              </li>
            ))}
          </ul>
        </ServerRow>
        <ServerRow label={t("adminApp.server.core")}>
          <p className="flex flex-wrap items-center gap-x-3">
            <span className="tabular">{data.core.version}</span>
            <span>
              {t("adminApp.server.upFor", {
                duration: format.duration((Date.now() - Date.parse(data.core.startedAt)) / 1000),
              })}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1",
                data.core.databaseUp ? "text-ok-fg" : "text-missing-fg",
              )}
            >
              {data.core.databaseUp ? (
                <CircleCheckIcon className="size-4" aria-hidden />
              ) : (
                <CircleXIcon className="size-4" aria-hidden />
              )}
              {data.core.databaseUp
                ? t("adminApp.server.databaseUp")
                : t("adminApp.server.databaseDown")}
            </span>
          </p>
        </ServerRow>
      </dl>
    </Card>
  );
}
